/**
 * Product URL Utilities
 * Handles SEO-friendly URL generation and parsing for product details pages
 * 
 * URL Format: /{skuId}/{variantSkuId}/{title-slug}
 * 
 * Examples:
 * - DB Product: /6964dff0319f3f5262a0c944/0/smart-watch-fitness
 * - API Product: /891359574592/12345/red-kar-watch-premium
 */

/**
 * Truncate and slugify title to max 11 words
 * @param {string} title - Product title
 * @returns {string} - URL-safe slug
 */
const slugifyTitle = (title) => {
  if (!title) return 'product';
  
  // Clean and split into words
  const words = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, ' ')          // Normalize spaces
    .trim()
    .split(' ')
    .filter(word => word.length > 0);
  
  // Take max 11 words and join with hyphens
  return words.slice(0, 11).join('-') || 'product';
};

/**
 * Generate SEO-friendly product URL
 * @param {Object} params - URL parameters
 * @param {string} params.id - MongoDB product ID (for DB products)
 * @param {string} params.offer - Alibaba offer ID (for API products)
 * @param {boolean} params.isAli - Whether it's an Alibaba product
 * @param {string} params.title - Product title for slug
 * @param {string} params.variantId - Variant SKU ID (optional, omit if not available)
 * @param {string} params.search - Search term (optional)
 * @returns {string} - SEO-friendly URL
 */
export const generateProductUrl = ({ id, offer, isAli, title, variantId, search }) => {
  const skuId = isAli ? offer : id;
  const slug = slugifyTitle(title);
  
  // Build base URL: /{skuId}/{variantSkuId}/{slug} or /{skuId}/{slug}
  let url;
  if (variantId && variantId !== '0') {
    url = `/${encodeURIComponent(skuId)}/${encodeURIComponent(variantId)}/${slug}`;
  } else {
    // No variant ID - use simpler URL format
    url = `/${encodeURIComponent(skuId)}/${slug}`;
  }
  
  // Add search as query param if present (keeps search functionality working)
  if (search) {
    url += `?search=${encodeURIComponent(search)}`;
  }
  
  return url;
};

/**
 * Parse SEO-friendly URL params to query params format
 * @param {Object} params - Route params from useParams()
 * @returns {Object} - Parsed params { id, ali, offer, variantId, slug }
 */
export const parseProductUrlParams = (params) => {
  const { skuId, variantId, slug } = params;
  
  // Determine if it's an Alibaba product based on ID format
  // MongoDB IDs are 24 character hex strings, Alibaba offer IDs are numeric
  const isMongoId = /^[a-f0-9]{24}$/i.test(skuId);
  const isAli = !isMongoId;
  
  return {
    id: isMongoId ? skuId : null,
    ali: isAli ? '1' : null,
    offer: isAli ? skuId : null,
    variantId: variantId || null, // null if not in URL
    slug: slug || '',
  };
};

/**
 * Check if current URL is legacy format (query params, /product-details/, or /p/)
 * @param {URLSearchParams} searchParams 
 * @param {string} pathname - Current pathname
 * @returns {boolean}
 */
export const isLegacyUrl = (searchParams, pathname = '') => {
  return searchParams.has('id') || searchParams.has('offer') || searchParams.has('ali') || pathname.startsWith('/product-details') || pathname.startsWith('/p/');
};

/**
 * Convert legacy URL params to SEO-friendly URL
 * Used for redirecting old URLs to new format
 * @param {URLSearchParams} searchParams 
 * @param {string} title - Product title (optional, will use 'product' if not provided)
 * @returns {string} - New SEO-friendly URL
 */
export const legacyToSeoUrl = (searchParams, title = '') => {
  const id = searchParams.get('id');
  const ali = searchParams.get('ali');
  const offer = searchParams.get('offer');
  const search = searchParams.get('search');
  
  return generateProductUrl({
    id,
    offer,
    isAli: ali === '1',
    title,
    search,
  });
};

export { slugifyTitle };

export default {
  generateProductUrl,
  parseProductUrlParams,
  isLegacyUrl,
  legacyToSeoUrl,
  slugifyTitle,
};
